<template>
  <Modal :closeable="true" :showModal="overrideModalOpen" @close="closeModal()">
    <template #header>
      <div>Test User Overrides</div>
    </template>
    <template #body>
      <p class="text-danger">** DISCLAIMER: This is a debugging and display manipulation tool and is not necessarily an accurate representation of production displays.</p>
      <div class="mb-3">
        <h6>Portal View</h6>
        <div v-for="option in overrideOptions" :key="option.displayTitle" class="override-item">
          <div class="form-check ms-3">
            <input type="radio" :id="option.displayTitle" :value="option.optionType" v-model="selectedOption" v-on:change="changeOption(option.optionType)" class="form-check-input">
            <label :for="option.displayTitle" class="form-check-label"> {{ option.displayTitle }}</label>
          </div>
        </div>
      </div>
      <div v-if="isQualifiedViewState && isOffersPage">
        <div class="mb-3">
          <h6>Marketplace <span class="text-danger"> **Can only test one at a time. When switching between them, ensure any deals are cleared or reset all of the overrides.</span></h6>
          <div v-for="option in overrideMarketplace" :key="option.displayTitle" class="override-item">
            <div class="form-check ms-3">
              <input type="radio" :id="option.displayTitle" :value="option.displayTitle" v-model="selectedLoanOfferType" class="form-check-input">
              <label :for="option.displayTitle" class="form-check-label"> {{ option.displayTitle }}</label>
            </div>
            <div class="ms-3" v-if="selectedLoanOfferType && selectedLoanOfferType === option.displayTitle">
              <h6 class="ms-4">Offer State</h6>
              <div v-for="(dealState, dealStateKey) in dealStates" :key="dealStateKey" class="override-item ms-4">
                <div>
                  <div class="form-check ms-3">
                    <input type="radio" :id="dealStateKey" :value="dealStateKey" v-model="selectedDealState" v-on:change="setCurrentDealState(dealStateKey)" class="form-check-input">
                    <label :for="dealStateKey" class="form-check-label">{{ dealState }}</label>
                  </div>
                  <div class="ms-4" v-if="['HAS_DEALS_MP', 'HAS_DEALS_FUNDED', 'HAS_DEALS_OFFER'].includes(selectedDealState) && dealStateKey === selectedDealState">
                    <div>
                      <div v-for="(dealOpt, dealKey) in dealOptions" :key="dealKey" class="override-item">
                        <div class="form-check ms-3">
                          <input type="radio" :id="dealKey" :value="dealKey" v-model="dealStage" v-on:change="changeDealStage(dealKey)" class="form-check-input">
                          <label :for="dealKey" class="form-check-label">{{ dealKey }}</label>
                        </div>
                        <div v-if="dealStage === dealKey">
                          <div v-for="status in dealOpt" :key="status" class="override-item ms-3">
                            <div class="form-check ms-3">
                              <input type="radio" :id="status" :value="status" v-model="dealStatus" v-on:change="changeDealStatus(status)" class="form-check-input">
                              <label :for="status" class="form-check-label">{{ status }}</label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="isQualifiedViewState">
        <div class="mb-3">
          <h6>Application State</h6>
          <div v-for="appState in applicationStates" :key="appState.displayTitle" class="override-item">
            <div class="form-check ms-3">
              <input type="radio" :id="appState.displayTitle" :value="appState.appStateValue" v-model="selectedAppState" v-on:change="setCurrentAppState(appState.appStateValue)" class="form-check-input">
              <label :for="appState.displayTitle" class="form-check-label">{{ appState.displayTitle }}</label>
            </div>
          </div>
          <div v-if="approvalsTest">
            <h6>Approval Dark Launch Features</h6>
            <div class="form-check form-switch mb-2">
              <input class="form-check-input" v-model="showAdjustmentCalculatorOverride" v-on:change="setAdjustmentCalculatorDarkLaunchState(showAdjustmentCalculatorOverride)" type="checkbox" role="switch" id="flexSwitchCheckDefault">
              <label class="form-check-label" for="flexSwitchCheckDefault">Adjustment Calculator</label>
            </div>
            <h6>Approval State</h6>
            <div v-for="(approvalValue, index) in approvalOptions" :key="'ao' + index" class="override-item">
              <div class="form-check ms-3">
                <input type="radio" :id="approvalValue" :value="approvalValue" v-model="selectedApprovalAmount" v-on:change="changeApproval(approvalValue)" class="form-check-input">
                <label :for="approvalValue" class="form-check-label">{{ approvalValue ? `${approvalValue} Approval(s)` : 'Default' }}</label>
              </div>
            </div>
            <h6>Is Renewal Deal?</h6>
            <div v-for="(renewalValue, index) in renewalOptions" :key="'r' + index" class="override-item">
              <div class="form-check ms-3">
                <input type="radio" :id="index" :value="renewalValue.name" v-model="selectedRenewalOption" v-on:change="setApprovalRenewal(renewalValue.name)" class="form-check-input">
                <label :for="renewalValue" class="form-check-label">{{ renewalValue.name }}</label>
              </div>
            </div>
            <h6>Lender Reviews</h6>
            <div class="form-check ms-3">
              <input type="checkbox" v-model="showLenderReviews" v-on:change="setLenderReviews(showLenderReviews)" class="form-check-input">
              <label class="form-check-label">{{ 'Show Lender Reviews' }}</label>
            </div>
            <br>
          </div>
          <div v-else>
            <h6>Offer State</h6>
            <div v-for="(dealState, dealStateKey) in dealStates" :key="dealStateKey" class="override-item">
              <div class="form-check ms-3">
                <input type="radio" :id="dealStateKey" :value="dealStateKey" v-model="selectedDealState" v-on:change="setCurrentDealState(dealStateKey)" class="form-check-input">
                <label :for="dealStateKey" class="form-check-label">{{ dealState }}</label>
              </div>
            </div>
          </div>
        </div>
        <div v-if="isOfferConfirmationPage" class="mb-3">
          <h6>Offer Stipulations</h6>
          <div class="form-check form-switch mb-2">
            <input class="form-check-input" v-model="offerStipulationExperiment" v-on:change="setStipulationExperiment(offerStipulationExperiment)" type="checkbox" role="switch" id="flexSwitchCheckDefault">
            <label class="form-check-label" for="flexSwitchCheckDefault">View Stipulation Experiment</label>
          </div>
          <div v-if="offerStipulationExperiment">
            <div v-for="option in stipulationOptions" :key="option" class="override-item">
              <div class="form-check ms-3">
                <input type="radio" :id="option" :value="option" v-model="stipulationOption" v-on:change="setOfferStipulationsOption(option)" class="form-check-input">
                <label :for="option" class="form-check-label">{{ option }}</label>
              </div>
            </div>
          </div>
        </div>
        <div class="mb-3">
          <h6>Add Deal</h6>
          <div v-for="(dealOpt, dealKey) in dealOptions" :key="dealKey" class="override-item">
            <div class="form-check ms-3">
              <input type="radio" :id="dealKey" :value="dealKey" v-model="dealStage" v-on:change="changeDealStage(dealKey)" class="form-check-input">
              <label :for="dealKey" class="form-check-label">{{ dealKey }}</label>
            </div>
            <div v-if="dealStage === dealKey">
              <div v-for="status in dealOpt" :key="status" class="override-item ms-3">
                <div class="form-check ms-3">
                  <input type="radio" :id="status" :value="status" v-model="dealStatus" v-on:change="changeDealStatus(status)" class="form-check-input">
                  <label :for="status" class="form-check-label">{{ status }}</label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="mb-3">
          <h6>Current Set Deals</h6>
          <div class="ms-3">{{ currentDealList }}</div>
        </div>
      </div>
      <div v-else-if="isUnqualifiedViewState && !isOffersPage">
        <h6>Scorecard Options</h6>
        <div v-for="scorecard in scorecardOptions" :key="scorecard.title" class="mb-3 ms-3">
          <div class='form-check'>
            <input type="radio" :id="scorecard.title" :value="scorecard.bvAlias" v-model="selectedScorecard" v-on:change="changeScorecard(scorecard.bvAlias)" class="form-check-input">
            <label :for="scorecard.title" class="form-check-label">{{ scorecard.title }}</label>
          </div>
          <div v-if="selectedScorecard === scorecard.bvAlias">
            <div v-for="scoreVal in scoreValues" :key="scoreVal" class="override-item">
              <div class='form-check ms-3'>
                <input type="radio" :id="scoreVal" :value="scoreVal" v-model="scoreValObj[selectedScorecard]" v-on:change="changeScoreVal(selectedScorecard, scoreVal)" class="form-check-input">
                <label :for="scoreVal" class="form-check-label">{{ scoreVal }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="modal-footer p-2">
        <button v-if="isQualifiedViewState" :disabled="!dealStatus || !dealStage" type="button" class="btn btn-primary" @click="setDealList({ status: dealStatus, stage: dealStage })">Add Deal</button>
        <button v-if="isQualifiedViewState" :disabled="!getOverrideDealList" type="button" class="btn btn-primary" @click="clearDealList">Clear Deals</button>
        <NuxtLink data-cy="route-to-owner-info" type="button" class="btn btn-primary" :to="'/embedded'">Owner Info</NuxtLink>
        <button type="button" class="btn btn-primary" @click="clearOverrides">Reset</button>
      </div>
    </template>
  </Modal>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import * as ScorecardLib from '~/libs/portal-scorecards'
import { DEAL_STATES, APPLICATIONS } from '~/libs/portal-constants'
import * as OverrideLib from '~/libs/user-override'
import Modal from '~/components/modal.vue'
import { usePortalStore } from '~/store/portal'
import { useOffersStore } from '~/store/offers'
import { useApprovalsStore } from '~/store/approvals'
import { useUserOverrideStore } from '~/store/user-override'

export default {
  name: 'UserOverrideModal',
  components: {
    Modal
  },
  data () {
    return {
      selectedOption: null,
      selectedLoanOfferType: null,
      selectedAppState: null,
      selectedDealState: null,
      selectedScorecard: null,
      selectedApprovalAmount: null,
      selectedRenewalOption: null,
      showAdjustmentCalculatorOverride: false,
      scoreValObj: {},
      scorecardOptions: ScorecardLib.scorecards,
      scoreValues: ScorecardLib.SCORE_VALUE_OPTIONS,
      dealStates: DEAL_STATES,
      dealOptions: OverrideLib.DEAL_STATUSES_BY_STAGE,
      dealStage: null,
      dealStatus: null,
      currentDeals: [],
      approvalOptions: [0, 1, 2, 3],
      renewalOptions: [
        {name: 'None'},
        {name: 'OnDeck ACH'},
      ],
      showLenderReviews: false,
      stipulationOptions: ['created', 'completed'],
      stipulationOption: 'created',
      offerStipulationExperiment: false,
    }
  },
  computed: {
    ...mapState(useUserOverrideStore, [
      // Getters
      'isQualifiedViewState',
      'isUnqualifiedViewState',
      'getOverrideDealList',
      // State
      'overrideModalOpen',
      'overrideOptions',
      'overrideMarketplace',
      'applicationStates',
      'currentViewState',
      'currentAppState',
      'approvalAmount',
      'stipulationExperiment',
    ]),
    ...mapState(usePortalStore, ['isQualifiedBorrower', 'portalInitialStep']),
    ...mapState(useOffersStore, ['marketplaceDeals']),

    currentDealList() {
      return this.marketplaceDeals.map(deal => [deal.stage, deal.status])
    },
    isOffersPage(){
      return this.$route.path.includes('/portal/offers')
    },
    approvalsTest() {
      return this.$route.path === '/portal/offers-2' || this.$route.path === '/portal' || this.$route.path === '/embedded/portal'
    },
    isOfferConfirmationPage() {
      return this.$route.path.includes('/portal/offers-2-confirmation')
    },
  },
  methods: {
    ...mapActions(useUserOverrideStore,
      [
        'setCurrentViewState',
        'clearCurrentViewState',
        'setCurrentAppState',
        'clearCurrentAppState',
        'setCurrentDealState',
        'clearCurrentDealState',
        'setCurrentScoreValues',
        'clearCurrentScoreValues',
        'setAdjustmentCalculatorDarkLaunchState',
        'setApprovalAmount',
        'setApprovalRenewal',
        'clearApprovalAmount',
        'clearApprovalRenewal',
        'setDealList',
        'clearDealList',
        'setLenderReviews',
        'setOfferStipulationsOption',
        'setStipulationExperiment',
        'clearAllOverrides'
      ]),
    ...mapActions(useApprovalsStore, ['requestApprovals']),

    closeModal() {
      useUserOverrideStore(this.$pinia).$patch({ overrideModalOpen: false })
    },
    changeOption(type) {
      this.setCurrentViewState(type)
      this.resetViewOption()
    },
    changeScorecard(scorecard) {
      this.selectedScorecard = scorecard
    },
    async changeApproval(value) {
      useApprovalsStore(this.$pinia).$patch({ approvals: [] })
      this.setApprovalAmount(value)
      this.requestApprovals()
    },
    changeScoreVal(scorecard, score) {
      this.scoreValObj[scorecard] = score
      this.setCurrentScoreValues(this.scoreValObj)
    },
    changeDealStage(stage) {
      this.dealStage = stage
    },
    changeDealStatus(status) {
      this.dealStatus = status
    },
    resetViewOption() {
      this.clearCurrentAppState()
      this.selectedAppState = null

      this.clearCurrentDealState()
      this.selectedDealState = null

      this.clearCurrentScoreValues()
      this.selectedScorecard = null
      this.scoreValObj = {}

      this.clearDealList()
      this.dealStage = null
      this.dealStatus = null
    },
    clearOverrides(){
      this.selectedOption = null
      this.selectedLoanOfferType = null
      this.clearAllOverrides()
      this.resetViewOption()
    },
  }
}
</script>
